import { api } from '@/lib/api/client';

export interface KnowledgeEntry {
  id: string;
  domain: string;
  title: string;
  content: string;
  tags: string[];
  entryMetadata: Record<string, unknown>;
  version: number;
  active: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateKnowledgeData {
  domain: string;
  title: string;
  content: string;
  tags: string[];
}

export interface UpdateKnowledgeData {
  domain?: string;
  title?: string;
  content?: string;
  tags?: string[];
  active?: boolean;
}

function mapResponse(data: any): KnowledgeEntry {
  return {
    id: data.id,
    domain: data.domain,
    title: data.title,
    content: data.content,
    tags: data.tags || [],
    entryMetadata: data.entry_metadata || {},
    version: data.version,
    active: data.active,
    createdBy: data.created_by,
    updatedBy: data.updated_by,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export const knowledgeService = {
  async list(params?: {
    domain?: string;
    activeOnly?: boolean;
    tag?: string;
  }): Promise<KnowledgeEntry[]> {
    const queryParams = new URLSearchParams();
    if (params?.domain) queryParams.set('domain', params.domain);
    if (params?.activeOnly !== undefined)
      queryParams.set('active_only', String(params.activeOnly));
    if (params?.tag) queryParams.set('tag', params.tag);

    const url = `/knowledge/${queryParams.toString() ? `?${queryParams}` : ''}`;
    const data = await api.get<any[]>(url);
    return data.map(mapResponse);
  },

  async get(id: string): Promise<KnowledgeEntry> {
    const data = await api.get<any>(`/knowledge/${id}`);
    return mapResponse(data);
  },

  async create(data: CreateKnowledgeData): Promise<KnowledgeEntry> {
    const result = await api.post<any>('/knowledge/', data);
    return mapResponse(result);
  },

  async update(
    id: string,
    data: UpdateKnowledgeData
  ): Promise<KnowledgeEntry> {
    const result = await api.patch<any>(`/knowledge/${id}`, data);
    return mapResponse(result);
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/knowledge/${id}`);
  },

  async activate(id: string): Promise<KnowledgeEntry> {
    const data = await api.post<any>(`/knowledge/${id}/activate`);
    return mapResponse(data);
  },

  async deactivate(id: string): Promise<KnowledgeEntry> {
    const data = await api.post<any>(`/knowledge/${id}/deactivate`);
    return mapResponse(data);
  },

  async search(query: string, domain?: string): Promise<KnowledgeEntry[]> {
    const params = new URLSearchParams({ query });
    if (domain) params.set('domain', domain);
    const data = await api.get<any[]>(`/knowledge/search?${params}`);
    return data.map(mapResponse);
  },
};
